import isUndefined from 'lodash-es/isUndefined';
import { findAncestor, isElement, isElementWithTagName, isText, isWhitespaceText } from './node';
import { isBreakElement } from './paragraph';

export function isCodeBlockElement(ancestor: Node): ancestor is HTMLPreElement {
  return isElementWithTagName(ancestor, 'pre');
}

export function isCodeBlockTrailingContent(node: Node, textOffset?: number): boolean {
  // If we are in the middle of a text node, check if the remaining text is only whitespace.
  if (isText(node) && !isUndefined(textOffset)) {
    const trailingText = node.data.substring(textOffset);
    return /^[\t\n\f\r ]*$/.test(trailingText);
  }

  return isWhitespaceText(node) || isBreakElement(node);
}

export interface CodeBlockCursor {
  codeBlockElement: HTMLPreElement;
  textNode: Text;
  cursorOffset: number;
}

export function codeBlockCursorByDeletingContents(
  container: Node,
  range: Range,
): CodeBlockCursor | undefined {
  const { ownerDocument: document } = container;
  if (!document) {
    return undefined;
  }

  // Return a cursor only if the start and end of the selection are in the same code block.
  const startCodeBlockElement = findAncestor(range.startContainer, isCodeBlockElement, container);
  const endCodeBlockElement = findAncestor(range.endContainer, isCodeBlockElement, container);
  if (!startCodeBlockElement || !endCodeBlockElement || startCodeBlockElement !== endCodeBlockElement) {
    return undefined;
  }

  // Delete the selection first, if it is non-empty.
  range.deleteContents();

  // Find or create a text node in the code block.
  const textNodeAndCursorOffset = findOrCreateTextNodeAndCursorOffset(
    document,
    range.startContainer,
    range.startOffset,
  );
  if (!textNodeAndCursorOffset) {
    return undefined;
  }

  const [textNode, cursorOffset] = textNodeAndCursorOffset;
  return { codeBlockElement: startCodeBlockElement, textNode, cursorOffset };
}

function findOrCreateTextNodeAndCursorOffset(
  document: Document,
  container: Node,
  offset: number,
): [Text, number] | undefined {
  // The cursor is on a text node.
  if (isText(container)) {
    return [container, offset];
  }

  if (!isElement(container)) {
    return undefined;
  }

  // Find the child node in front of the cursor.
  const childNode = offset < container.childNodes.length ? container.childNodes[offset] : null;

  // The cursor is in front of the <code> element, recurse.
  if (isElementWithTagName(childNode, 'code')) {
    return findOrCreateTextNodeAndCursorOffset(document, childNode, 0);
  }

  // The cursor is in front of a text node.
  if (isText(childNode)) {
    return [childNode, 0];
  }

  // The cursor is behind a text node.
  if (childNode && isText(childNode.previousSibling)) {
    return [childNode.previousSibling, childNode.previousSibling.length];
  }

  // The cursor is in front of something else, or at the end of the container.
  const newTextNode = document.createTextNode('');
  container.insertBefore(newTextNode, childNode);
  return [newTextNode, 0];
}
